import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SUPPORTED_LANGUAGES } from "../../i18n";
import { useAltPaths } from "../../context/AltPathContext.jsx";

const LANGUAGE_META = {
  de: { flag: "🇩🇪", label: "Deutsch" },
  en: { flag: "🇬🇧", label: "English" },
};

export default function LanguageSwitcher({ lang }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { dePath, enPath, hasTranslation } = useAltPaths();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function handlePointerDown(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    function handleKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  function switchTo(targetLang) {
    setOpen(false);
    if (targetLang === lang) return;

    // The current page isn't available in the other language at all (e.g.
    // an untranslated fallback) — land on that language's 404 page instead
    // of silently redirecting to its home page.
    if (hasTranslation === false) {
      navigate(`/${targetLang}/404`);
      return;
    }

    // dePath/enPath come from the current page's own SEOHead call — the
    // same sibling path it already computes for hreflang tags. Most
    // routes translate their slug per language (e.g. /was-ist-larp vs
    // /what-is-larp, /impressum vs /legal-notice), so a naive /de//en
    // prefix swap lands on a 404 for anything but the home page.
    const targetPath = targetLang === "de" ? dePath : enPath;
    if (targetPath != null) {
      navigate(`/${targetLang}${targetPath}${location.search}`);
      return;
    }

    // Fallback for the brief window before SEOHead's effect has run.
    const rest = location.pathname.replace(`/${lang}`, "");
    navigate(`/${targetLang}${rest}${location.search}`);
  }

  const current = LANGUAGE_META[lang] ?? { flag: "🌐", label: lang };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Language switcher: ${lang.toUpperCase()}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          background: "transparent",
          border: "1px solid rgba(254,250,239,0.2)",
          borderRadius: "var(--radius-pill)",
          color: "var(--color-cream-soft)",
          fontFamily: "var(--font-mono)",
          fontSize: "12px",
          padding: "7px 12px",
          cursor: "pointer",
        }}
      >
        <span aria-hidden="true" style={{ fontSize: "14px" }}>
          {current.flag}
        </span>
        <span>{lang.toUpperCase()}</span>
        <span aria-hidden="true" style={{ fontSize: "9px", color: "var(--color-cream-faint)" }}>
          {open ? "▴" : "▾"}
        </span>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Language switcher"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: "150px",
            background: "var(--color-bg-raised)",
            border: "1px solid rgba(254,250,239,0.14)",
            borderRadius: "10px",
            padding: "6px",
            margin: 0,
            listStyle: "none",
            zIndex: 30,
          }}
        >
          {SUPPORTED_LANGUAGES.map((code) => {
            const meta = LANGUAGE_META[code] ?? { flag: "🌐", label: code };
            const isActive = code === lang;
            return (
              <li key={code} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => switchTo(code)}
                  disabled={isActive}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    width: "100%",
                    background: isActive ? "rgba(254,250,239,0.08)" : "transparent",
                    border: "none",
                    borderRadius: "6px",
                    color: "var(--color-cream)",
                    fontSize: "13px",
                    padding: "8px 10px",
                    cursor: isActive ? "default" : "pointer",
                    textAlign: "left",
                  }}
                >
                  <span aria-hidden="true" style={{ fontSize: "15px" }}>
                    {meta.flag}
                  </span>
                  <span>{meta.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
